import React, { useMemo, useState } from 'react';
import apiClient from '../lib/apiClient';

const COMMON_PASSWORDS = new Set([
    'password', 'password123', 'qwerty', 'qwerty123', '123456', '12345678',
    '123456789', '1234567890', 'abc123', 'admin', 'admin123', 'welcome',
    'welcome123', 'letmein', 'iloveyou', 'monkey', 'dragon', 'passw0rd',
]);

const normalizePasswordForComparison = (password = '') =>
    password.normalize('NFKC').replace(/\s+/g, '').toLocaleLowerCase();

const getPasswordChecks = (password = '', confirmPassword = '') => ({
    hasMinLength: Array.from(password).length >= 12,
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[1-9]/.test(password),
    hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    isNotCommonPassword: !COMMON_PASSWORDS.has(normalizePasswordForComparison(password)),
    hasPrintableCharactersOnly: /^[\x21-\x7E]+$/.test(password),
    matchesConfirmation: confirmPassword.length > 0 && password === confirmPassword,
});

const Requirement = ({ met, label }) => {
    const color = met === true ? '#1E7A3E' : met === false ? '#D81F26' : '#6E6E6E';
    const icon = met === true ? '✓' : met === false ? '✕' : '○';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color, fontSize: '13px' }}>
            <span>{icon}</span>
            <span>{label}</span>
        </div>
    );
};

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
    const passwordChecks = getPasswordChecks(password, confirmPassword);
    const passwordMeetsPolicy =
        passwordChecks.hasMinLength &&
        passwordChecks.hasLowercase &&
        passwordChecks.hasNumber &&
        passwordChecks.hasSpecialChar &&
        passwordChecks.isNotCommonPassword &&
        passwordChecks.hasPrintableCharactersOnly;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setMessage('');
        setError('');
        try {
            if (!token) throw new Error('This reset link is invalid or incomplete.');
            if (!passwordMeetsPolicy) throw new Error('Password does not meet the security requirements.');
            if (!passwordChecks.matchesConfirmation) throw new Error('Retyped password must match.');

            const response = await apiClient.post('/api/reset-password', {
                token,
                password,
                confirmPassword,
            });
            setMessage(response.data?.message || 'Password updated successfully. You can sign in now.');
            setPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Unable to reset password.');
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #3457D5 0%, #F28C46 100%)', padding: '20px' },
        card: { background: '#FFFFFF', borderRadius: '24px', padding: '48px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
        logo: { display: 'block', width: '64px', height: '64px', objectFit: 'contain', margin: '0 auto 12px' },
        title: { fontSize: '28px', fontWeight: '700', color: '#2B2B2B', marginBottom: '8px', textAlign: 'center' },
        subtitle: { fontSize: '15px', color: '#6E6E6E', marginBottom: '28px', textAlign: 'center', lineHeight: 1.6 },
        input: { width: '100%', padding: '12px 16px', fontSize: '15px', border: '1px solid #E5E5E5', borderRadius: '12px', marginBottom: '16px', outline: 'none' },
        button: { width: '100%', padding: '16px', background: '#3457D5', color: '#FFFFFF', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
        info: { padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px', lineHeight: 1.5 },
        error: { background: '#FFE0E0', color: '#D81F26' },
        success: { background: '#E7F7EC', color: '#1E7A3E' },
        passwordPanel: { background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' },
        passwordPanelTitle: { fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' },
        footer: { marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#6E6E6E' },
        link: { color: '#3457D5', textDecoration: 'none', fontWeight: '600' },
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <img src="/corpusio-logo.webp" alt="Corpusio" style={styles.logo} />
                <h1 style={styles.title}>Choose a new password</h1>
                <p style={styles.subtitle}>
                    Set a fresh password for your Corpusio account using the reset link from your email.
                </p>
                {error && <div style={{ ...styles.info, ...styles.error }}>{error}</div>}
                {message && <div style={{ ...styles.info, ...styles.success }}>{message}</div>}
                <form onSubmit={handleSubmit}>
                    <input type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} required style={styles.input} />
                    <input type="password" placeholder="Retype new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={styles.input} />
                    <div style={styles.passwordPanel}>
                        <div style={styles.passwordPanelTitle}>Password requirements</div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                            <Requirement met={password ? passwordChecks.hasMinLength : null} label="At least 12 characters" />
                            <Requirement met={password ? passwordChecks.hasLowercase : null} label="Include at least one lowercase letter (a-z)" />
                            <Requirement met={password ? passwordChecks.hasNumber : null} label="Include at least one number (1-9)" />
                            <Requirement met={password ? passwordChecks.hasSpecialChar : null} label="Include at least one special character" />
                            <Requirement met={password ? passwordChecks.isNotCommonPassword : null} label="Avoid common passwords" />
                            <Requirement met={password || confirmPassword ? passwordChecks.matchesConfirmation : null} label="Retyped password must match" />
                            <Requirement met={password ? passwordChecks.hasPrintableCharactersOnly : null} label="Use only ASCII characters with no spaces" />
                        </div>
                    </div>
                    <button type="submit" style={styles.button} disabled={loading}>
                        {loading ? 'Updating password...' : 'Update password'}
                    </button>
                </form>
                <div style={styles.footer}>
                    Back to <a href="/login" style={styles.link}>sign in</a>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
